import { BoxOfferDetailedEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferInvitationStatus, BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { type BoxOfferInvitationCoupon, type BoxOfferInvitationDto } from "@modules/boxOffer/gateway/boxOfferDto"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@modules/core/utils/i18n"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import dayjs from "dayjs"

export type BoxOfferInvitationState = Omit<
  BoxOfferInvitationEntity,
  "getColorStatus" | "getPriceAfterDicount" | "getWordingStatus" | "getWordingCouponDate"
>
export class BoxOfferInvitationEntity {
  id: string
  boxOfferId: string
  invitee: UserEntity
  inviter: UserEntity
  status: BoxOfferInvitationStatus
  createdAt: Date
  coupon: BoxOfferInvitationCoupon | null
  deferredDate: Date | null
  subscriptionEndDate: Date | null
  updatedAtDate: Date | null
  outOfBand: boolean

  private constructor(invitation: BoxOfferInvitationState) {
    this.id = invitation.id
    this.boxOfferId = invitation.boxOfferId
    this.invitee = invitation.invitee
    this.inviter = invitation.inviter
    this.status = invitation.status
    this.createdAt = invitation.createdAt
    this.coupon = invitation.coupon
    this.deferredDate = invitation.deferredDate
    this.subscriptionEndDate = invitation.subscriptionEndDate
    this.updatedAtDate = invitation.updatedAtDate
    this.outOfBand = invitation.outOfBand
  }

  static fromDto(dto: BoxOfferInvitationDto): BoxOfferInvitationEntity {
    return new BoxOfferInvitationEntity({
      ...dto,
      invitee: UserEntity.fromDto(dto.invitee),
      inviter: UserEntity.fromDto(dto.inviter),
      createdAt: dayjs(dto.createdAt).toDate(),
      deferredDate: dto.deferredDate ? dayjs(dto.deferredDate).toDate() : null,
      subscriptionEndDate: dto.subscriptionEndDate ? dayjs(dto.subscriptionEndDate).toDate() : null,
      updatedAtDate: dto.updatedAtDate ? dayjs(dto.updatedAtDate).toDate() : null,
    })
  }

  getColorStatus = () => {
    switch (this.status) {
      case BoxOfferInvitationStatus.PENDING:
        return "text-custom-yellow-500"
      case BoxOfferInvitationStatus.ACCEPTED:
        return "text-textSuccess"
      case BoxOfferInvitationStatus.REFUSED:
      case BoxOfferInvitationStatus.CANCELLED:
        return "text-red-500"
    }
  }

  getWordingStatus = () => {
    switch (this.status) {
      case BoxOfferInvitationStatus.PENDING:
        return word("box.members.tab.offer.status.pendingInvitation")
      case BoxOfferInvitationStatus.ACCEPTED:
        return word("box.members.tab.offer.status.ongoing")
      case BoxOfferInvitationStatus.REFUSED:
        return word("box.members.tab.offer.status.declinedInvitation")
      case BoxOfferInvitationStatus.CANCELLED:
        return word("box.members.tab.offer.status.canceledInvitation")
      default:
        return ""
    }
  }

  getWordingCouponDate(offer: BoxOfferDetailedEntity): string {
    if (!this.coupon || offer.type !== BoxOfferType.Subscription) return ""

    if (this.coupon.durationInMonths === null) {
      return word("boxOffer.invitation.coupon.date.unlimited")
    }
    return word("boxOffer.invitation.coupon.date.durationInMonths", { count: this.coupon.durationInMonths })
  }

  getPriceAfterDicount = (price: number): number => {
    if (!this.coupon || this.coupon.value <= 0) return price

    if (this.coupon.type === CouponType.PERCENT) {
      return price - (price * this.coupon.value) / 100
    } else {
      return price - this.coupon.value
    }
  }
}
